import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLog, devError } from './utils/secureLogger';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';
import ProfileAvatar from './components/ProfileAvatar';

const BACKEND_URL = API_URL;

const AdminStudentDetail = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('verified'); // 'verified', 'application', 'admin'

  // NACME Admin edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [writeAccessEnabled, setWriteAccessEnabled] = useState(false);

  // Fetch student details
  useEffect(() => {
    const fetchStudentDetail = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = getValidToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/admin/students/${studentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Student not found');
          }
          throw new Error(`Failed to fetch student: ${response.statusText}`);
        }

        const data = await response.json();
        setStudent(data);
        devLog('Fetched student detail:', data);
      } catch (err) {
        devError('Error fetching student detail:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudentDetail();
  }, [studentId, navigate]);

  // Fetch write access setting
  useEffect(() => {
    const fetchWriteAccessSetting = async () => {
      try {
        const token = getValidToken();
        if (!token) return;

        const response = await fetch(`${BACKEND_URL}/api/admin/settings/nacme_admin_write_access`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setWriteAccessEnabled(data.setting_value === 'true');
        }
      } catch (err) {
        devError('Error fetching write access setting:', err);
      }
    };

    fetchWriteAccessSetting();
  }, []);

  // Helper to format display values
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return value;
  };

  // Helper to format dates (parse as local date to avoid timezone shifts)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Check for dummy dates (1900-01-01, 2000-01-01) and display as N/A
    if (dateString.startsWith('1900-01-01') || dateString.startsWith('2000-01-01')) {
      return 'N/A';
    }

    // If it's just a date (YYYY-MM-DD), parse it as local to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // For datetime strings, use normal parsing
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle field edit - useCallback to prevent re-creating function on each render
  const handleFieldEdit = useCallback((fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const token = getValidToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/students/${studentId}/nacme-admin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedFields)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save changes');
      }

      // Update local student state with edited values
      setStudent(prev => ({
        ...prev,
        ...editedFields
      }));

      setSaveSuccess(true);
      setIsEditMode(false);
      setEditedFields({});

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      devError('Error saving NACME Admin fields:', err);
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditMode(false);
    setEditedFields({});
    setSaveError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading student details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-xl font-semibold text-red-600">{error}</p>
          <div className="mt-4 flex gap-3 justify-center">
            <button
              onClick={() => navigate('/admin/student-records')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Back to Student Records
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!student) return null;

  // Section Component
  const Section = ({ title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  // Field Component
  const Field = ({ label, value, fullWidth = false, isMatchingField = false }) => (
    <div className={`${fullWidth ? "md:col-span-2" : ""} ${
      isMatchingField
        ? 'bg-green-50 dark:bg-green-900/20 p-3 rounded-md border border-green-200 dark:border-green-800'
        : ''
    }`}>
      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
        {label}
      </label>
      <p className="text-gray-900 dark:text-white">
        {formatValue(value)}
      </p>
    </div>
  );

  // Editable Field Component for NACME Admin tab
  const EditableField = ({ label, fieldName, value, fullWidth = false }) => {
    // Get the current value from editedFields if it exists, otherwise use the original value
    const currentValue = editedFields.hasOwnProperty(fieldName)
      ? editedFields[fieldName]
      : (value || '');

    // Prevent parent re-render by stopping event propagation
    const handleChange = (e) => {
      const newValue = e.target.value;
      // Use requestAnimationFrame to defer state update
      requestAnimationFrame(() => {
        handleFieldEdit(fieldName, newValue);
      });
    };

    return (
      <div className={fullWidth ? "md:col-span-2" : ""}>
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
          {label}
        </label>
        {isEditMode ? (
          <input
            type="text"
            defaultValue={currentValue}
            onBlur={(e) => handleFieldEdit(fieldName, e.target.value)}
            autoComplete="off"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        ) : (
          <p className="text-gray-900 dark:text-white">
            {formatValue(value)}
          </p>
        )}
      </div>
    );
  };

  // Document Link Component
  const DocumentLink = ({ label, url }) => {
    const handleDocumentClick = async (e) => {
      e.preventDefault();

      if (!url || url === 'N/A' || url === '0' || url === 'null') {
        return;
      }

      try {
        const token = getValidToken();
        if (!token) {
          navigate('/login');
          return;
        }

        // Map label to document type
        const documentType = label.toLowerCase();

        // Fetch presigned URL from backend
        const response = await fetch(
          `${BACKEND_URL}/api/admin/students/${studentId}/document/${documentType}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to get document URL');
        }

        const data = await response.json();

        // Open document in new tab
        window.open(data.url, '_blank');
      } catch (err) {
        devError('Error opening document:', err);
        alert('Failed to open document. Please try again.');
      }
    };

    const hasDocument = url && url !== 'N/A' && url !== '0' && url !== 'null';

    return (
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
          {label}
        </label>
        {hasDocument ? (
          <button
            onClick={handleDocumentClick}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline inline-flex items-center gap-1"
          >
            View {label}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">Not available</p>
        )}
      </div>
    );
  };

  // Document Icon Link Component - For quick access in tabs area
  const DocumentIconLink = ({ label, url, icon }) => {
    const handleDocumentClick = async (e) => {
      e.preventDefault();

      if (!url || url === 'N/A' || url === '0' || url === 'null') {
        return;
      }

      try {
        const token = getValidToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const documentType = label.toLowerCase();

        const response = await fetch(
          `${BACKEND_URL}/api/admin/students/${studentId}/document/${documentType}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to get document URL');
        }

        const data = await response.json();
        window.open(data.url, '_blank');
      } catch (err) {
        devError('Error opening document:', err);
        alert('Failed to open document. Please try again.');
      }
    };

    const buttonStyles = icon === 'resume'
      ? 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800'
      : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800';

    const iconTextStyles = icon === 'resume'
      ? 'text-blue-700 dark:text-blue-300'
      : 'text-purple-700 dark:text-purple-300';

    return (
      <button
        onClick={handleDocumentClick}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${buttonStyles}`}
        title={`View ${label}`}
      >
        {icon === 'resume' ? (
          <svg className={`w-5 h-5 ${iconTextStyles}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ) : (
          <svg className={`w-5 h-5 ${iconTextStyles}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <span className={`text-sm font-medium ${iconTextStyles}`}>{label}</span>
        <svg className={`w-4 h-4 ${iconTextStyles}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
    );
  };

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.STUDENT_RECORDS}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate('/admin/student-records')}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Student Records
            </button>

            <button
              onClick={() => navigate('/admin')}
              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Back to Admin
            </button>
          </div>

          <div className="flex items-start gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {student.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">Student ID: {student.id}</p>
            </div>

            {/* Headshot using ProfileAvatar component */}
            <div className="flex-shrink-0">
              <ProfileAvatar
                name={student.name}
                headshot={student.headshot}
                size="xl"
              />
            </div>
          </div>

          {/* Matching Data Summary - Only show if verified data exists */}
          {(student.current_university_verified || student.major_verified || student.current_academic_class_verified || student.gpa_verified) && (
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md border border-green-200 dark:border-green-800">
              {student.current_university_verified && (
                <span>
                  <span className="font-semibold">University:</span> {student.current_university_verified}
                </span>
              )}
              {student.major_verified && (
                <span>
                  {student.current_university_verified && ', '}
                  <span className="font-semibold">Major:</span> {student.major_verified}
                </span>
              )}
              {student.current_academic_class_verified && (
                <span>
                  {(student.current_university_verified || student.major_verified) && ', '}
                  <span className="font-semibold">Class Year:</span> {student.current_academic_class_verified}
                </span>
              )}
              {student.gpa_verified && (
                <span>
                  {(student.current_university_verified || student.major_verified || student.current_academic_class_verified) && ', '}
                  <span className="font-semibold">GPA:</span> {student.gpa_verified}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Status Badges */}
        <div className="flex flex-col gap-2">
          <div className={`px-3 py-1 text-sm rounded ${
            student.partner_has_verified_status === 'verified'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : student.partner_has_verified_status === 'partial'
              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
          }`}>
            <div className="font-medium">
              {student.partner_has_verified_status === 'verified'
                ? 'Verified'
                : student.partner_has_verified_status === 'partial'
                ? 'Partially Verified'
                : 'Pending Verification'}
            </div>
            {student.verified_by && student.partner_has_verified_status !== 'pending' && (
              <div className="text-xs mt-1 opacity-90">
                by {student.verified_by}
              </div>
            )}
          </div>
          {student.eligible25 && (
            <span className="px-3 py-1 text-sm rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
              Eligible 2025
            </span>
          )}
          {student.applied_for_this_cycle === 'Yes' ? (
            <span className="px-3 py-1 text-sm rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
              Applied This Cycle
            </span>
          ) : (
            <span className="px-3 py-1 text-sm rounded bg-red-600 text-white">
              No Application This Cycle
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex items-center space-x-8">
            <button
              onClick={() => setActiveTab('verified')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'verified'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              University Verified Record
            </button>
            <button
              onClick={() => setActiveTab('application')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'application'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Student Application Record
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'admin'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              NACME Admin Record
            </button>

            {/* Document Quick Access Icons */}
            {(student.resume || student.resume_verified) && (
              <DocumentIconLink
                label="Resume"
                url={student.resume || student.resume_verified}
                icon="resume"
              />
            )}
            {(student.transcript || student.transcript_verified) && (
              <DocumentIconLink
                label="Transcript"
                url={student.transcript || student.transcript_verified}
                icon="transcript"
              />
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'verified' && (
        <>
          {/* University Verified Record Tab */}
          <Section title="Basic Information">
            <Field label="First Name" value={student.first_name_verified_sp25} />
            <Field label="Last Name" value={student.last_name_verified_sp25} />
            <Field label="University Email" value={student.university_email_verified} />
            <Field label="Gender" value={student.gender_verified} />
            <Field label="Citizenship Status" value={student.citizenship_status_verified_sp25} />
          </Section>

          <Section title="Demographic Information">
            <Field label="Race" value={student.race_verified} />
            <Field label="Ethnicity" value={student.ethnicity_verified} />
            <Field label="Heritage" value={student.heritage_verified} />
            <Field label="Multiracial" value={student.multiracial_verified} />
            <Field label="Latinx Heritage" value={student.latinx_heritage_verified} />
          </Section>

          <Section title="Academic Information">
            <Field label="Current University" value={student.current_university_verified} isMatchingField={true} />
            <Field label="Major" value={student.major_verified} isMatchingField={true} />
            <Field label="Major at Matriculation" value={student.major_at_matriculation_verified_sp25} />
            <Field label="Current GPA" value={student.gpa_verified} isMatchingField={true} />
            <Field label="Cumulative GPA at Graduation" value={student.cumulative_gpa_at_graduation_verified_sp25} />
            <Field label="Current Academic Class" value={student.current_academic_class_verified} isMatchingField={true} />
            <Field label="Expected Graduation Date" value={formatDate(student.expected_grad_date_verified_sp25)} />
            <Field label="Matriculation Date" value={formatDate(student.matriculation_date_verified_sp25)} />
            <Field label="Conferred Graduation Date" value={formatDate(student.conferred_grad_date_verified_sp25)} />
            <Field label="Date of Initial Block Term" value={formatDate(student.date_of_initial_block_term_verified_sp25)} />
          </Section>

          <Section title="Scholarship Information">
            <Field label="Block Grant Scholar" value={student.block_grant_scholar_verified_sp25} />
            <Field label="Corporate Scholar" value={student.corporate_scholar_verified_sp25} />
            <Field label="Special Scholarship" value={student.special_scholarship_verified_sp25} />
            <Field label="NVB Scholar" value={student.nvb_scholar_verified_sp25} />
          </Section>

          <Section title="Status Information">
            <Field label="Applicant Status" value={student.applicant_status_verified_sp25} />
            <Field label="Withdraw/Reject Reason" value={student.withdraw_reject_reason_verified} />
            <Field label="Verification Status" value={student.partner_has_verified_status} />
            <Field label="Verified At" value={formatDate(student.verified_at)} />
            <Field label="Verified By" value={student.verified_by} />
            <Field label="Eligible 2025" value={student.eligible25} />
            <Field label="Applied This Cycle" value={student.applied_for_this_cycle} />
            <Field label="Internal/External" value={student.internal_external} />
          </Section>

          <Section title="Documents">
            <DocumentLink label="Resume" url={student.resume_verified} />
            <DocumentLink label="Transcript" url={student.transcript_verified} />
          </Section>

          <Section title="Account Activity">
            <Field label="Account Active" value={student.is_account_active} />
            <Field label="Account Created" value={formatDate(student.account_created_at)} />
            <Field label="Last Login" value={formatDate(student.last_login)} />
            <Field label="Total Logins" value={student.total_logins} />
            <Field label="Record Updated" value={formatDate(student.updated_at)} />
          </Section>

          <Section title="System Information">
            <Field label="University Partner ID" value={student.university_partner_id} />
            <Field label="Latitude" value={student.latitude} />
            <Field label="Longitude" value={student.longitude} />
            <Field label="Geocoding Status" value={student.geocoding_status} />
            <Field label="Coordinates Updated" value={formatDate(student.coordinates_updated_at)} />
          </Section>
        </>
      )}

      {activeTab === 'application' && (
        <>
          {/* Student Application Record Tab - Original content */}
          <Section title="Basic Information">
        <Field label="First Name" value={student.first_name} />
        <Field label="Middle Name" value={student.middle_name} />
        <Field label="Last Name" value={student.last_name} />
        <Field label="Date of Birth" value={formatDate(student.date_of_birth)} />
        <Field label="Gender" value={student.gender} />
        <Field label="Citizenship Status" value={student.citizenship_status} />
        <Field label="State of Birth" value={student.state_of_birth} />
        <Field label="T-Shirt Size" value={student.tshirt} />
      </Section>

      {/* Contact Information */}
      <Section title="Contact Information">
        <Field label="Personal Email" value={student.personal_email} />
        <Field label="University Email" value={student.university_email_verified || student.university_email} />
        <Field label="Cell Phone" value={student.cell_phone} />
        <Field label="Alternate Phone" value={student.alternate_phone} />
        <Field label="Street Address" value={student.street} fullWidth />
        <Field label="Apartment/Unit" value={student.apartment} />
        <Field label="City" value={student.city} />
        <Field label="State" value={student.state} />
        <Field label="ZIP Code" value={student.zip} />
      </Section>

      {/* Demographic Information */}
      <Section title="Demographic Information">
        <Field label="Race" value={student.race} />
        <Field label="Ethnicity" value={student.ethnicity} />
        <Field label="Heritage" value={student.heritage} />
        <Field label="Multiracial" value={student.multiracial_verified} />
        <Field label="Latinx Heritage" value={student.latinx_heritage_verified} />
        <Field label="First Generation College Student" value={student.first_generation_college_student} />
        <Field label="Parent 1 Education" value={student.parent_1_education} />
        <Field label="Parent 2 Education" value={student.parent_2_education} />
      </Section>

      {/* High School Background */}
      <Section title="High School Background">
        <Field label="High School Graduation Date" value={formatDate(student.high_school_grad_date)} />
        <Field label="High School GPA" value={student.high_school_gpa} />
        <Field label="SAT Score" value={student.sat_score} />
        <Field label="ACT Score" value={student.act_score} />
        <Field label="Community College" value={student.community_college} />
        <Field label="Bridge Program" value={student.bridge_program} />
      </Section>

      {/* Current Academic Information */}
      <Section title="Current Academic Information">
        <Field label="University" value={student.current_university} />
        <Field label="Degree Type" value={student.degree_type} />
        <Field label="Major" value={student.major} />
        <Field label="Specific Major" value={student.specific_major} />
        <Field label="Current Academic Class" value={student.current_academic_class} />
        <Field label="Current GPA" value={student.current_gpa} />
        <Field label="GPA Scale" value={student.current_gpa_scale} />
        <Field label="College Start Date" value={formatDate(student.college_start_date)} />
        <Field label="Expected Graduation Date" value={formatDate(student.expected_grad_date)} />
        <Field label="Enrollment Year" value={student.enrollment_year} />
        <Field label="Undergraduate Research" value={student.undergrad_research} />
        <Field label="Additional University" value={student.additional_university} />
      </Section>

      {/* Work & Financial */}
      <Section title="Work & Financial Information">
        <Field label="Job in Engineering" value={student.job_in_engineering} />
        <Field label="Current/Recent Employer" value={student.current_recent_employer} />
        <Field label="Financial Aid" value={student.financial_aid} />
        <Field label="Scholarships/Programs" value={student.scholarships_programs} />
      </Section>

      {/* Verification & Status */}
      <Section title="Verification & Status">
        <Field label="Verification Status" value={student.partner_has_verified_status} />
        <Field label="Verified At" value={formatDate(student.verified_at)} />
        <Field label="Verified By" value={student.verified_by} />
        <Field label="Applicant Status" value={student.applicant_status_verified} />
        <Field label="Eligible 2025" value={student.eligible25} />
        <Field label="Applied This Cycle" value={student.applied_for_this_cycle} />
        <Field label="Internal/External" value={student.internal_external} />
      </Section>

      {/* SP25 Verified Information */}
      {(student.first_name_verified_sp25 || student.block_grant_scholar_verified_sp25 || student.corporate_scholar_verified_sp25) && (
        <Section title="Spring 2025 Verified Information">
          <Field label="First Name (SP25)" value={student.first_name_verified_sp25} />
          <Field label="Last Name (SP25)" value={student.last_name_verified_sp25} />
          <Field label="Block Grant Scholar" value={student.block_grant_scholar_verified_sp25} />
          <Field label="Corporate Scholar" value={student.corporate_scholar_verified_sp25} />
          <Field label="Special Scholarship" value={student.special_scholarship_verified_sp25} />
          <Field label="NVB Scholar" value={student.nvb_scholar_verified_sp25} />
          <Field label="Applicant Status (SP25)" value={student.applicant_status_verified_sp25} />
          <Field label="Matriculation Date (SP25)" value={formatDate(student.matriculation_date_verified_sp25)} />
          <Field label="Expected Grad Date (SP25)" value={formatDate(student.expected_grad_date_verified_sp25)} />
          <Field label="Major at Matriculation (SP25)" value={student.major_at_matriculation_verified_sp25} />
          <Field label="Cumulative GPA at Graduation (SP25)" value={student.cumulative_gpa_at_graduation_verified_sp25} />
        </Section>
      )}

      {/* Documents */}
      <Section title="Documents">
        <DocumentLink label="Resume" url={student.resume} />
        <DocumentLink label="Transcript" url={student.transcript} />
        <DocumentLink label="Headshot" url={student.headshot} />
      </Section>

      {/* Consents */}
      <Section title="Consents & Permissions">
        <Field label="Data Consent" value={student.data_consent} />
        <Field label="Likeness Consent" value={student.likeness_consent} />
      </Section>

      {/* Account Activity */}
      <Section title="Account Activity">
        <Field label="Account Active" value={student.is_account_active} />
        <Field label="Account Created" value={formatDate(student.account_created_at)} />
        <Field label="Last Login" value={formatDate(student.last_login)} />
        <Field label="Total Logins" value={student.total_logins} />
        <Field label="Record Created" value={formatDate(student.created_at)} />
        <Field label="Record Updated" value={formatDate(student.updated_at)} />
      </Section>

      {/* System Information */}
      <Section title="System Information">
        <Field label="University Partner ID" value={student.university_partner_id} />
        <Field label="Latitude" value={student.latitude} />
        <Field label="Longitude" value={student.longitude} />
        <Field label="Geocoding Status" value={student.geocoding_status} />
      </Section>
        </>
      )}

      {activeTab === 'admin' && (
        <>
          {/* Edit/Save/Cancel Buttons */}
          <div className="mb-6 flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div>
              {saveSuccess && (
                <p className="text-green-600 dark:text-green-400 font-medium">
                  ✓ Changes saved successfully
                </p>
              )}
              {saveError && (
                <p className="text-red-600 dark:text-red-400 font-medium">
                  ✗ {saveError}
                </p>
              )}
              {!writeAccessEnabled && !isEditMode && (
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Write access is disabled. Enable in Platform Settings to edit these fields.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {!isEditMode ? (
                <button
                  onClick={() => setIsEditMode(true)}
                  disabled={!writeAccessEnabled}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    writeAccessEnabled
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Edit Fields
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || Object.keys(editedFields).length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>

          <Section title="Scholarship Information">
            <EditableField label="Block Phase" fieldName="block_phase_verified" value={student.block_phase_verified} />
            <EditableField label="Disbursement Date" fieldName="disbursement_date_verified" value={student.disbursement_date_verified} />
            <EditableField label="Scholarship Type" fieldName="scholarship_type_verified" value={student.scholarship_type_verified} />
            <EditableField label="Scholarship Category" fieldName="scholarship_category_verified" value={student.scholarship_category_verified} />
            <EditableField label="Scholarship" fieldName="scholarship_verified" value={student.scholarship_verified} />
            <EditableField label="Nomination Cycle" fieldName="nomination_cycle_verified" value={student.nomination_cycle_verified} />
            <EditableField label="Spending Plan Template" fieldName="spending_plan_template_verified" value={student.spending_plan_template_verified} />
            <EditableField label="Budget Category" fieldName="budget_category_verified" value={student.budget_category_verified} />
          </Section>

          <Section title="Financial Disbursements">
            <EditableField label="NACME University Remittance" fieldName="nacme_university_remittance_verified" value={student.nacme_university_remittance_verified} />
            <EditableField label="Block Grant Scholarship Remittance" fieldName="block_grant_scholarship_remittance_verified" value={student.block_grant_scholarship_remittance_verified} />
            <EditableField label="Block Grant Scholarship" fieldName="block_grant_scholarship_verified" value={student.block_grant_scholarship_verified} />
            <EditableField label="University Remittance" fieldName="university_remittance_verified" value={student.university_remittance_verified} />
            <EditableField label="Student Direct Payment" fieldName="student_direct_pmt_verified" value={student.student_direct_pmt_verified} />
            <EditableField label="Amount Open" fieldName="amount_open_verified" value={student.amount_open_verified} />
            <EditableField label="Program Support" fieldName="program_support_verified" value={student.program_support_verified} />
          </Section>

          <Section title="Corporate Scholar Payments">
            <EditableField label="Corporate Scholar - Fall Actual Payment to Student" fieldName="corporate_scholar_fall_actual_payment_to_student_verified" value={student.corporate_scholar_fall_actual_payment_to_student_verified} />
            <EditableField label="Corporate Scholar - Total for University Reporting" fieldName="corporate_scholar_total_for_university_reporting_verified" value={student.corporate_scholar_total_for_university_reporting_verified} />
            <EditableField label="Spring Corporate Scholars - Spring Balance Remaining Owed" fieldName="spring_corporate_scholars_spring_balance_remaining_owed_verifie" value={student.spring_corporate_scholars_spring_balance_remaining_owed_verifie} />
          </Section>

          <Section title="Chevron Payments">
            <EditableField label="Chevron - University Remit Payment to Scholar" fieldName="chevron_university_remit_payment_to_scholar_verified" value={student.chevron_university_remit_payment_to_scholar_verified} />
            <EditableField label="Spring Chevron - NACME Paid Scholar Directly" fieldName="spring_chevron_nacme_paid_scholar_directly_verified" value={student.spring_chevron_nacme_paid_scholar_directly_verified} />
          </Section>

          <Section title="Ford Payments">
            <EditableField label="Ford - University Remit Payment to Scholar" fieldName="ford_university_remit_payment_to_scholar_verified" value={student.ford_university_remit_payment_to_scholar_verified} />
            <EditableField label="Spring Ford - NACME Paid Scholar Directly" fieldName="spring_ford_nacme_paid_scholar_directly_verified" value={student.spring_ford_nacme_paid_scholar_directly_verified} />
            <EditableField label="Ford Balance Remaining" fieldName="ford_balance_remaining_verified" value={student.ford_balance_remaining_verified} />
          </Section>

          <Section title="GE Edison Payments">
            <EditableField label="GE Edison - University Remit Payment to Scholar" fieldName="ge_edison_university_remit_payment_to_scholar_verified" value={student.ge_edison_university_remit_payment_to_scholar_verified} />
            <EditableField label="GE Edison - NACME Paid Scholar Directly" fieldName="ge_edison_nacme_paid_scholar_directly_verified" value={student.ge_edison_nacme_paid_scholar_directly_verified} />
            <EditableField label="GE Edison Balance Remaining" fieldName="ge_edison_balance_remaining_verified" value={student.ge_edison_balance_remaining_verified} />
          </Section>

          <Section title="University Account Administration">
            <EditableField label="Account Admin First Name" fieldName="account_admin_first_name_verified" value={student.account_admin_first_name_verified} />
            <EditableField label="Account Admin Last Name" fieldName="account_admin_last_name_verified" value={student.account_admin_last_name_verified} />
            <EditableField label="University Account Admin Email" fieldName="university_account_admin_email_verified" value={student.university_account_admin_email_verified} />
          </Section>

          <Section title="Admin Notes">
            <EditableField label="Comments/Notes" fieldName="comments_notes_verified" value={student.comments_notes_verified} fullWidth={true} />
            <EditableField label="NACME Notes" fieldName="nacme_notes_verified" value={student.nacme_notes_verified} fullWidth={true} />
            <EditableField label="Internal ID" fieldName="internal_id_verified" value={student.internal_id_verified} />
          </Section>
        </>
      )}
      </div>
    </AdminRouteGuard>
  );
};

export default AdminStudentDetail;
